import type { JobFileEntry } from '@jobos/shared';

import { JobFileCard, type JobFileActions } from './JobFileCard';

interface JobFilesGridProps extends Omit<JobFileActions, 'onView'> {
  jobId: string;
  files: JobFileEntry[];
  /**
   * @param ordered The set as this view shows it — the viewer pages through that
   * order, so what comes next matches what the user was just scrolling past.
   */
  onView?: (file: JobFileEntry, ordered: JobFileEntry[]) => void;
  /** The ticked files. Omit — along with `onToggleSelect` — for a grid with no selection. */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (id: string) => void;
}

/**
 * The default view of the job file library.
 *
 * Two columns at 390px — wide enough that a photo is recognisable one-handed,
 * and the column count climbs from there rather than the cards growing.
 */
export function JobFilesGrid({
  jobId,
  files,
  selectedIds,
  onToggleSelect,
  onView,
  onEdit,
  onMove,
  onDelete,
}: JobFilesGridProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {files.map((file) => (
        <JobFileCard
          key={file.id}
          jobId={jobId}
          file={file}
          selected={selectedIds?.has(file.id) ?? false}
          {...(onToggleSelect ? { onToggleSelect } : {})}
          onView={onView && ((viewed) => onView(viewed, files))}
          onEdit={onEdit}
          onMove={onMove}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}
